import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePaymentAlerts } from '../hooks/usePaymentAlerts';
import './PaymentAlertsCard.css';

const PaymentAlertsCard = () => {
  const navigate = useNavigate();
  const { overdue, dueSoon, totalAlerts, isLoading } = usePaymentAlerts();

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}/${month}/${year}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  const calculateDaysUntilDue = (dueDate) => {
    // Parsear fecha de vencimiento
    let dueDateStr;
    if (typeof dueDate === 'string') {
      dueDateStr = dueDate.split('T')[0]; // Extraer solo la fecha si viene con hora
    } else {
      dueDateStr = new Date(dueDate).toISOString().split('T')[0];
    }
    
    // Comparar con hoy usando strings para evitar problemas de zona horaria
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    const todayDate = new Date(todayStr + 'T00:00:00');
    const dueDateParsed = new Date(dueDateStr + 'T00:00:00');
    const diffTime = dueDateParsed - todayDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)); // Usar floor en lugar de ceil
    return diffDays;
  };

  if (isLoading) {
    return (
      <div className="payment-alerts-card">
        <div className="loading">Cargando alertas...</div>
      </div>
    );
  }

  if (totalAlerts === 0) {
    return null;
  }

  const overdueTotal = overdue.reduce((sum, p) => 
    sum + (parseFloat(p.amount) - parseFloat(p.paid_amount || 0)), 0
  );
  const dueSoonTotal = dueSoon.reduce((sum, p) => 
    sum + (parseFloat(p.amount) - parseFloat(p.paid_amount || 0)), 0
  );

  return (
    <div className="payment-alerts-card">
      <div className="payment-alerts-card-header">
        <h3>🔔 Alertas de Pagos</h3>
        <span className="alerts-summary">
          {totalAlerts} alerta{totalAlerts !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="payment-alerts-card-content">
        {/* Resumen */}
        <div className="alerts-summary-grid">
          {overdue.length > 0 && (
            <div className="summary-item overdue">
              <div className="summary-icon">⚠️</div>
              <div className="summary-info">
                <div className="summary-label">Vencidos</div>
                <div className="summary-value">{overdue.length}</div>
                <div className="summary-amount">{formatCurrency(overdueTotal)}</div>
              </div>
            </div>
          )}
          {dueSoon.length > 0 && (
            <div className="summary-item due-soon">
              <div className="summary-icon">⏰</div>
              <div className="summary-info">
                <div className="summary-label">Próximos a vencer</div>
                <div className="summary-value">{dueSoon.length}</div>
                <div className="summary-amount">{formatCurrency(dueSoonTotal)}</div>
              </div>
            </div>
          )}
        </div>

        {/* Lista de alertas urgentes */}
        <div className="alerts-urgent-list">
          {overdue.length > 0 && (
            <div className="urgent-section">
              <h4 className="urgent-title overdue">
                ⚠️ Pagos Vencidos ({overdue.length})
              </h4>
              {overdue.slice(0, 3).map((payment) => {
                const remaining = parseFloat(payment.amount) - parseFloat(payment.paid_amount || 0);
                return (
                  <div
                    key={payment.id}
                    className="urgent-item overdue"
                    onClick={() => navigate(`/payments?status=overdue`)}
                  >
                    <div className="urgent-item-info">
                      <strong>
                        {payment.student?.full_name || 
                         payment.enrollment?.student?.full_name || 
                         `Matrícula #${payment.enrollment_id}`}
                      </strong>
                      <span>Vence: {formatDate(payment.due_date)}</span>
                    </div>
                    <div className="urgent-item-amount">
                      {formatCurrency(remaining)}
                    </div>
                  </div>
                );
              })}
              {overdue.length > 3 && (
                <button
                  className="btn-view-more"
                  onClick={() => navigate('/payments?status=overdue')}
                >
                  Ver {overdue.length - 3} más...
                </button>
              )}
            </div>
          )}

          {dueSoon.length > 0 && (
            <div className="urgent-section">
              <h4 className="urgent-title due-soon">
                ⏰ Próximos a Vencer ({dueSoon.length})
              </h4>
              {dueSoon.slice(0, 3).map((payment) => {
                const remaining = parseFloat(payment.amount) - parseFloat(payment.paid_amount || 0);
                const daysUntil = calculateDaysUntilDue(payment.due_date);
                // No mostrar si ya está vencido (días negativos)
                if (daysUntil < 0) return null;
                const daysText = daysUntil === 0 
                  ? 'Vence hoy' 
                  : `Vence en ${daysUntil} día${daysUntil !== 1 ? 's' : ''}`;
                return (
                  <div
                    key={payment.id}
                    className="urgent-item due-soon"
                    onClick={() => navigate(`/payments?status=pending`)}
                  >
                    <div className="urgent-item-info">
                      <strong>
                        {payment.student?.full_name || 
                         payment.enrollment?.student?.full_name || 
                         `Matrícula #${payment.enrollment_id}`}
                      </strong>
                      <span>
                        {daysText}: {formatDate(payment.due_date)}
                      </span>
                    </div>
                    <div className="urgent-item-amount">
                      {formatCurrency(remaining)}
                    </div>
                  </div>
                );
              }).filter(Boolean)}
              {dueSoon.length > 3 && (
                <button
                  className="btn-view-more"
                  onClick={() => navigate('/payments?status=pending')}
                >
                  Ver {dueSoon.length - 3} más...
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="payment-alerts-card-footer">
        <button
          className="btn btn-primary"
          onClick={() => navigate('/payments')}
        >
          Ver Todos los Pagos
        </button>
      </div>
    </div>
  );
};

export default PaymentAlertsCard;

